/**
 * notificationRoutes.js
 *
 * Mapea una notificación in-app (shape `{type, title, meta}`) y el rol del
 * usuario actual a la ruta destino de la aplicación. Devuelve `null` si la
 * notificación no tiene un destino válido — en ese caso la UI debe solo
 * marcarla como leída sin navegar.
 *
 * El mapa agrupa por dominio (payouts, pedidos, chat, productos, planes,
 * vendedor/docs, PQR, seguridad). Está construido a partir del inventario
 * documentado en /app/memory/PRD.md § Notifications routing map.
 */

/**
 * @param {{type?: string, title?: string, meta?: Record<string, any>}} n
 * @param {'buyer' | 'seller' | 'admin' | undefined} role
 * @returns {string | null}  Ruta de destino o `null` si no hay destino válido.
 */
export function routeForNotification(n, role) {
  const t = (n && n.type) || '';
  const m = (n && n.meta) || {};
  const title = ((n && n.title) || '').toLowerCase();

  // ─── DIAN / Factus ──────────────────────────────────────────────────────
  if (t.startsWith('admin.dian.')) return '/admin/facturacion-dian';

  // ─── PAYOUTS ────────────────────────────────────────────────────────────
  if (t === 'admin.payout.retry_exhausted' || t === 'admin.payout.stuck') {
    return m.payout_id
      ? `/admin/payouts?payout_id=${encodeURIComponent(m.payout_id)}`
      : '/admin/payouts';
  }
  if (
    t === 'payout.sent' ||
    t === 'payout.scheduled' ||
    t === 'payout.completed' ||
    t === 'payout.held' ||
    t.startsWith('seller.payout.')
  ) {
    return m.payout_id
      ? `/vendedor/finanzas?payout_id=${encodeURIComponent(m.payout_id)}`
      : '/vendedor/finanzas';
  }

  // ─── PEDIDOS ────────────────────────────────────────────────────────────
  if (
    t === 'buyer.order.paid' ||
    t === 'buyer.invoice.available' ||
    t === 'buyer.refund.issued'
  ) {
    return m.order_id
      ? `/mis-pedidos?order_id=${encodeURIComponent(m.order_id)}`
      : '/mis-pedidos';
  }
  if (t === 'seller.order.paid') {
    return m.order_id
      ? `/vendedor/pedidos?order_id=${encodeURIComponent(m.order_id)}`
      : '/vendedor/pedidos';
  }
  if (t === 'order') {
    // Legacy — no diferencia de rol
    const base = role === 'seller' ? '/vendedor/pedidos' : '/mis-pedidos';
    return m.order_id ? `${base}?order_id=${encodeURIComponent(m.order_id)}` : base;
  }

  // ─── PRODUCTOS / PROMOS ─────────────────────────────────────────────────
  if (t === 'seller.product.out_of_stock' || t.startsWith('seller.promo.')) {
    return m.product_id
      ? `/producto/${encodeURIComponent(m.product_id)}`
      : '/vendedor/productos';
  }
  if (t === 'buyer.promo.offer') {
    return m.product_id
      ? `/producto/${encodeURIComponent(m.product_id)}`
      : '/ofertas';
  }

  // ─── PLANES / ACTIVACIÓN ────────────────────────────────────────────────
  if (t.startsWith('seller.activation.') || t.startsWith('seller.plan.')) {
    return '/vendedor/suscripcion';
  }

  // ─── VENDEDOR / ONBOARDING / DOCS ───────────────────────────────────────
  if (t === 'seller.approved' || t === 'seller.rejected') {
    return '/vendedor/onboarding';
  }
  if (t === 'admin.seller.onboarding_submitted') {
    return m.seller_id
      ? `/admin/vendedores?seller_id=${encodeURIComponent(m.seller_id)}`
      : '/admin/vendedores';
  }

  // ─── CHAT ───────────────────────────────────────────────────────────────
  if (t === 'buyer.chat.new' || t.startsWith('seller.chat.')) {
    const base = role === 'seller' ? '/vendedor/mensajes' : '/mensajes';
    return m.thread_id ? `${base}?thread_id=${encodeURIComponent(m.thread_id)}` : base;
  }

  // ─── PQR ────────────────────────────────────────────────────────────────
  if (t === 'admin.pqr.new') {
    return m.pqr_id
      ? `/admin/pqr?pqr_id=${encodeURIComponent(m.pqr_id)}`
      : '/admin/pqr';
  }

  // ─── SEGURIDAD ──────────────────────────────────────────────────────────
  if (t.startsWith('security.')) {
    return '/perfil';
  }

  // ─── LEGACY / GENERIC (heurística por título) ───────────────────────────
  if (t === 'generic' || !t) {
    if (title.includes('retiro')) {
      return role === 'admin' ? '/admin/retiros' : '/vendedor/wallet';
    }
    if (title.includes('documentos') || title.includes('verific')) {
      return '/vendedor/onboarding';
    }
    if (title.includes('comisión') || title.includes('comision')) {
      return '/vendedor/suscripcion';
    }
    if (title.includes('pago parcial')) {
      return m.payout_id
        ? `/vendedor/finanzas?payout_id=${encodeURIComponent(m.payout_id)}`
        : '/vendedor/finanzas';
    }
  }

  // ─── Sin destino → solo marcar como leída ───────────────────────────────
  return null;
}
